import type { IntelCategory } from '@badger-sentinel/rules';
import { INTEL_CATEGORIES } from '@badger-sentinel/rules';
import type { IntelReportWire } from '@badger-sentinel/protocol';

import { criticalityOf, type IntelCriticality } from './intel.js';

/**
 * Turn deltas — **two received reports, and never a guess** (§4's QA line).
 *
 * ## Why this is its own module
 *
 * It imports `IntelReportWire` and `./intel.js` and nothing else. It therefore
 * *cannot* see a `CellViewWire`, which makes the phase's hardest constraint
 * structural rather than remembered: there is no snapshot in scope to diff.
 *
 * That matters because the obvious implementation is the forbidden one. The
 * design's delta block wants escalation movement, force status and detection
 * gains, and every one of those is a field on `CellView` — so the natural reading
 * is "subtract last turn's snapshot from this one". The store deliberately does
 * not keep last turn's snapshot (M5-D9: the view is replaced wholesale and
 * nothing is retained), so that subtraction would have to invent its left-hand
 * side, and a delta computed against a guess renders identically to one computed
 * against a fact.
 *
 * ## What a two-report delta actually is
 *
 * A report is a list of things that happened *to this seat in one turn*. So the
 * comparable quantities are **counts over entries**, and the interesting ones are
 * the four an operator would ask for out loud:
 *
 *  - how much happened, and how much of it was critical;
 *  - which categories moved;
 *  - what the turn did to the escalation counter (`ESCALATION_*` carry `amount`);
 *  - how many detections changed (`DETECTION_CHANGED`, `CHARACTERIZED`).
 *
 * Escalation is worth a note. `theatre.escalationCounter` is on every snapshot
 * and would give a *level*, but not a movement — the movement is in the
 * `ESCALATION_BANKED` / `APPLIED` / `DECAYED` entries, which are in the report
 * this seat was actually sent. So the counter is read where it belongs (the
 * status row's meter, Phase 1) and the *change* is read here, from entries.
 *
 * ## What is not here
 *
 * - **VP** (F1). The design's delta block has four VP bindings and every one of
 *   them is a number this build cannot compute: §12's evaluator is M6. The chrome
 *   slot is reserved and stays reserved, and no VP figure appears in this module,
 *   in the panel it feeds, or anywhere else on this screen.
 * - **Zone activity.** The report carries no location channel. `fields.region`
 *   exists on exactly one event type and reading it as a column would be the
 *   reader inventing a schema out of a payload key.
 * - **Force status as a strength figure.** `STATUS_CHANGED` and `ASSET_DESTROYED`
 *   are counted as what they are — entries — and never totalled into an order of
 *   battle. The roster rail is the order of battle.
 */

/** One measure, this turn against last. `previous` is null when there is none. */
export interface DeltaMeasure {
  readonly key: string;
  readonly now: number;
  /** Null where there is no predecessor — never zero, which is a comparison. */
  readonly previous: number | null;
}

export interface CategoryDelta {
  readonly category: IntelCategory;
  readonly now: number;
  readonly previous: number | null;
}

export interface IntelDelta {
  readonly turn: number;
  /** The turn compared against, or null: **the panel says so rather than showing zeroes.** */
  readonly againstTurn: number | null;
  readonly measures: readonly DeltaMeasure[];
  readonly categories: readonly CategoryDelta[];
}

const CRITICALITY_KEYS: readonly IntelCriticality[] = Object.freeze(['CRITICAL', 'PRIORITY']);

const DETECTION_TYPES: ReadonlySet<string> = new Set(['DETECTION_CHANGED', 'CHARACTERIZED']);

const ESCALATION_TYPES: ReadonlySet<string> = new Set([
  'ESCALATION_BANKED',
  'ESCALATION_APPLIED',
  'ESCALATION_DECAYED',
]);

/**
 * `ESCALATION_DECAYED` takes the counter *down*, and the payload's `amount` is
 * the size of the move rather than its sign — `{amount: 2, quietTurns: 1}` in the
 * shipped fixture is a decay of two. So the sign is applied here, from the type,
 * which is the one thing that says which way the counter went.
 */
function escalationMove(report: IntelReportWire): number {
  let total = 0;
  for (const entry of report.entries) {
    if (!ESCALATION_TYPES.has(entry.type)) continue;
    const amount = entry.fields.amount;
    if (typeof amount !== 'number') continue;
    total += entry.type === 'ESCALATION_DECAYED' ? -amount : amount;
  }
  return total;
}

function countsOf(report: IntelReportWire): Readonly<Record<string, number>> {
  const counts: Record<string, number> = {
    ENTRIES: report.entries.length,
    CRITICAL: 0,
    PRIORITY: 0,
    DETECTION: 0,
    ESCALATION: escalationMove(report),
  };
  for (const entry of report.entries) {
    const level = criticalityOf(entry.type);
    if (CRITICALITY_KEYS.includes(level)) counts[level] = (counts[level] ?? 0) + 1;
    if (DETECTION_TYPES.has(entry.type)) counts.DETECTION = (counts.DETECTION ?? 0) + 1;
  }
  return counts;
}

const MEASURE_KEYS: readonly string[] = Object.freeze([
  'ENTRIES',
  'CRITICAL',
  'PRIORITY',
  'DETECTION',
  'ESCALATION',
]);

/**
 * This report against its predecessor.
 *
 * `previous` may be null in three ways that are all the same thing to this
 * function and are **not** the same thing to the panel: turn 1 has no turn 0; a
 * seat that has just joined has not fetched the older report; the older turn's
 * fetch is still in flight. In every one of them the measures carry
 * `previous: null` and `againstTurn` is null, so nothing can render a `+0` or an
 * arrow — the assertion is that no delta is drawn from one report.
 *
 * A caller passing the *same* report as both arguments is refused for the same
 * reason: a delta computed against itself is all zeroes and reads exactly like a
 * quiet turn.
 */
export function intelDelta(current: IntelReportWire, previous: IntelReportWire | null): IntelDelta {
  const now = countsOf(current);
  const comparable = previous !== null && previous.turn !== current.turn ? previous : null;
  const before = comparable === null ? null : countsOf(comparable);

  const measures = MEASURE_KEYS.map((key) => ({
    key,
    now: now[key] ?? 0,
    previous: before === null ? null : (before[key] ?? 0),
  }));

  const present = new Set<IntelCategory>([
    ...current.entries.map((entry) => entry.category),
    ...(comparable?.entries ?? []).map((entry) => entry.category),
  ]);

  const categories = INTEL_CATEGORIES.filter((category) => present.has(category)).map(
    (category) => ({
      category,
      now: current.entries.filter((entry) => entry.category === category).length,
      previous:
        comparable === null
          ? null
          : comparable.entries.filter((entry) => entry.category === category).length,
    }),
  );

  return {
    turn: current.turn,
    againstTurn: comparable === null ? null : comparable.turn,
    measures,
    categories,
  };
}

/** `+3`, `−2`, `0` — the movement, or null where there is nothing to compare to. */
export function movementOf(measure: DeltaMeasure | CategoryDelta): number | null {
  return measure.previous === null ? null : measure.now - measure.previous;
}

export function signedText(value: number): string {
  if (value === 0) return '0';
  return value > 0 ? `+${value}` : `−${Math.abs(value)}`;
}
